import cx from 'classnames'
import isNil from 'lodash/isNil'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { Link } from 'features/i18n/router'
import { Post, Thread } from 'lib/_types'
import { selectIsLoggedIn } from 'store/selectors'
import { ROUTE_PATH } from 'lib/paths'
import { addNotification } from 'store/slices/notifications'
import { useNavigate } from 'features/i18n/router'
import {
  useGetThreadsJoinedStatusesQuery,
  useJoinThreadMutation,
} from 'store/api/yay/thread'
import { useCurrentUser } from 'store/api/yay/user'

import PostAvatar from '../PostItem/PostAvatar'
import PostUserName from '../PostItem/PostUserName'
import PostTime from '../PostItem/PostTime'
import ImageLoader from 'components/ImageLoader'
import Button from 'components/Button'

import './style.scss'

const PostThread = ({
  post,
  thread,
  disabled,
}: {
  post?: Post
  thread?: Thread
  disabled?: boolean
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const isLoggedIn = useSelector(selectIsLoggedIn)
  const { data: currentUser } = useCurrentUser()

  const _thread =
    post?.shared_thread ??
    thread ??
    (post?.post_type == 'shareable_thread'
      ? post?.shareable?.['posts/thread']
      : null)

  const threadsJoinedStatusesRes = useGetThreadsJoinedStatusesQuery(
    {
      ids: [_thread?.id],
    },
    {
      skip:
        !isLoggedIn ||
        isNil(_thread?.id) ||
        post?.post_type != 'shareable_thread' ||
        _thread?.owner?.id == currentUser?.id ||
        !isNil(post?._threadRelationship),
    }
  )

  // `post?._threadRelationship` is only available in timeline pages.
  // For other pages, like post, conversation, or reposts,
  // we shall rely on `threadsJoinedStatusesRes`.
  const threadsJoinedStatuses =
    post?._threadRelationship ?? threadsJoinedStatusesRes?.data?.[_thread?.id]

  const [joinThread, joinThreadRes] = useJoinThreadMutation()

  if (isNil(_thread)) {
    return null
  }

  return (
    <div
      className={cx('PostThread', {
        'PostThread--disabled': disabled,
        'PostThread--shareable-thread': post?.post_type == 'shareable_thread',
      })}
    >
      <div className="PostThread__wrapper">
        <Link
          className={cx('PostThread__header', {
            'PostThread__header--no-link': !isLoggedIn,
          })}
          to={ROUTE_PATH.GROUP_THREAD(post?.group_id, _thread?.id)}
          onClick={(e) => {
            if (!isLoggedIn) {
              e.preventDefault()
            }
          }}
        >
          {_thread?.thread_icon ? (
            <ImageLoader
              className="PostThread__header__img"
              url={_thread?.thread_icon}
            />
          ) : (
            <img
              className="PostThread__header__icon"
              src="/images/icon-post-thread-header-balloon.svg"
            />
          )}

          <p className="PostThread__header__p">{_thread?.title}</p>
        </Link>

        {(() => {
          const lastPost = _thread?.last_post

          return (
            <div className="PostThread__body">
              {isNil(lastPost) ? (
                <p className="PostThread__body__p">
                  {t('post.thread.group_thread_no_post')}
                </p>
              ) : (
                <div className="PostThread__body__last-post">
                  <div className="PostThread__body__last-post__header">
                    <PostAvatar
                      user={lastPost?.user}
                      isConversationLayoutSmall={false}
                    />

                    <PostUserName user={lastPost?.user} />
                    <PostTime createdAt={lastPost?.created_at} />
                  </div>

                  <p className="PostThread__body__last-post__p">
                    {lastPost?.post_type == 'image'
                      ? t('post.thread.thread_info_user_posted_image')
                      : lastPost?.post_type == 'video'
                      ? t('post.thread.thread_info_user_posted_video')
                      : lastPost?.post_type == 'shareable_url'
                      ? t('post.thread.thread_info_user_shared_url')
                      : lastPost?.post_type == 'youtube'
                      ? t('post.thread.thread_info_user_shared_youtube')
                      : lastPost?.text}
                  </p>
                </div>
              )}
            </div>
          )
        })()}
      </div>

      {threadsJoinedStatuses == 'unjoined' && (
        <div className="PostThread__buttons">
          <Button
            loading={joinThreadRes.isLoading}
            onClick={() => {
              joinThread({
                thread: _thread as Thread,
                userId: currentUser?.id?.toString?.(),
              }).then((res) => {
                if (res?.['data']?.response == 200) {
                  navigate(ROUTE_PATH.GROUP_THREAD(post?.group_id, _thread?.id))

                  dispatch(
                    addNotification({
                      message: t('group_threads_page.thread_joined'),
                    })
                  )
                }
              })
            }}
          >
            {t('group_threads_page.join_thread_long')}
          </Button>
        </div>
      )}
    </div>
  )
}

export default PostThread
